import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, UserCheck, Shuffle, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const BulkAssignDialog = ({ open, onClose, leadIds, onDone, allowUnassign = false }) => {
  const [users, setUsers] = useState([]);
  const [mode, setMode] = useState('single'); // single | round_robin
  const [single, setSingle] = useState('');
  const [selectedRR, setSelectedRR] = useState([]); // ids for round-robin
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/leads/assignable-users`);
      setUsers((res.data || []).filter((u) => ['comercial', 'chefe_equipa'].includes(u.role)));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (open) { load(); setMode('single'); setSingle(''); setSelectedRR([]); } }, [open, load]);

  const toggleRR = (id) => setSelectedRR((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { lead_ids: leadIds, mode };
      if (mode === 'single') {
        if (!single) { toast.error('Escolhe um comercial'); setSaving(false); return; }
        payload.assigned_to = single;
      } else {
        if (selectedRR.length === 0) { toast.error('Escolhe pelo menos um comercial'); setSaving(false); return; }
        payload.comercial_ids = selectedRR;
      }
      const res = await axios.post(`${API_URL}/leads/bulk-assign`, payload);
      toast.success(res.data?.message || 'Leads atribuídas');
      onDone && onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao atribuir');
    } finally {
      setSaving(false);
    }
  };

  const doUnassign = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/leads/bulk-assign`, { lead_ids: leadIds, mode: 'unassign' });
      toast.success(res.data?.message || 'Lead(s) postas por atribuir');
      onDone && onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao desatribuir');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-[#2D3E6F]" /> {allowUnassign ? 'Reatribuir' : 'Atribuir'} {leadIds.length} lead(s)</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setMode('single')} className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${mode === 'single' ? 'border-[#2D3E6F] bg-[#2D3E6F]/5 text-[#2D3E6F]' : 'border-slate-200 text-slate-500'}`} data-testid="mode-single-btn">
            <UserCheck className="w-4 h-4" /> Um comercial
          </button>
          <button type="button" onClick={() => setMode('round_robin')} className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${mode === 'round_robin' ? 'border-[#2D3E6F] bg-[#2D3E6F]/5 text-[#2D3E6F]' : 'border-slate-200 text-slate-500'}`} data-testid="mode-rr-btn">
            <Shuffle className="w-4 h-4" /> Distribuir
          </button>
        </div>

        {mode === 'single' ? (
          <div className="mt-2">
            <p className="text-xs text-slate-500 mb-1.5">Todas as leads selecionadas vão para o mesmo comercial.</p>
            <Select value={single} onValueChange={setSingle}>
              <SelectTrigger data-testid="bulk-single-select"><SelectValue placeholder="Escolher comercial" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.role === 'chefe_equipa' ? ' (Chefe Equipa)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="mt-2">
            <p className="text-xs text-slate-500 mb-1.5">As leads são distribuídas em rotação (round-robin) entre os comerciais escolhidos.</p>
            <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-lg divide-y">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`rr-user-${u.id}`}>
                  <input type="checkbox" checked={selectedRR.includes(u.id)} onChange={() => toggleRR(u.id)} className="w-4 h-4 accent-[#2D3E6F]" />
                  {u.name}{u.role === 'chefe_equipa' ? ' (Chefe Equipa)' : ''}
                </label>
              ))}
              {users.length === 0 && <p className="px-3 py-3 text-xs text-slate-400">Sem comerciais disponíveis.</p>}
            </div>
            {selectedRR.length > 0 && <p className="text-[11px] text-slate-400 mt-1.5">{leadIds.length} leads ÷ {selectedRR.length} comerciais ≈ {Math.ceil(leadIds.length / selectedRR.length)} cada</p>}
          </div>
        )}

        <DialogFooter className={allowUnassign ? 'sm:justify-between' : ''}>
          {allowUnassign && (
            <Button variant="ghost" onClick={doUnassign} disabled={saving} className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" data-testid="dialog-unassign-btn">
              Pôr por atribuir
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2D3E6F] hover:bg-[#1a2a4a]" data-testid="bulk-assign-confirm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}{allowUnassign ? 'Reatribuir' : 'Atribuir'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
